import { Flex } from 'foundations-components/components/Flex';
import { SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import { TalkToSalesFallback } from '../../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useGlobalLoading } from '../../../../hooks/loadingHooks';
import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import { useAppSelector } from '../../../../reducers';
import { isUngatedForInbound25Selector } from '../../../../selectors/GatingSelectors';
import { productSelector } from '../../../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../../../utils/urls/appQueryParamUtils';
import { MerchCardLayoutUI } from '../../../../v2/components/MerchCardLayoutUI';
import ProductIcon from '../../../common/ProductIcon';
import { default as DetailedTermSelector } from '../../../common/TermSelectors/DetailedTermSelector';
import { ProductCardBadge } from '../../../common/badges/ProductCardBadge';
import { default as SkeletonLoadWrapper } from '../../../common/loading/SkeletonLoadWrapper';
import ErrorBoundary from '../../../error-boundary/ErrorBoundary';
import RequiredOnboardingSubtextContainer from '../../../plan-builder/RequiredOnboardingSubtextContainer';
import { SuiteFeatures } from '../../features-display/SuiteFeatures';
import { ProductTileFeaturesContainer } from '../../features-display/features-display-containers/ProductTileFeaturesContainer';
import { PriceDisplayContainer } from '../../price-display/PriceDisplayContainer';
import { IncludedSeatsDisplayContainerV1 } from '../../seats-display/IncludedSeatsDisplayContainerV1';
import { ProductCardCTAs } from './ProductCardCTAs';
import { useProductCardMetadata } from './useProductCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ProductCard = ({
  apiName,
  screen,
  trackingSource,
  columnStart,
  title,
  description,
  productNameOverride,
  skipInterstitialPage = false
}) => {
  const isAppReady = useGlobalLoading();
  const isMobile = useIsMobile();
  const product = useAppSelector(state => {
    if (!isAppReady) return;
    return productSelector(state, {
      apiName
    });
  });
  const {
    skuId,
    productLevel,
    productLine,
    shouldShowTermSelector,
    purchaseMotions,
    hasHigherTieredProduct,
    isHubspotFree,
    shouldShowSuiteObjectives
  } = useProductCardMetadata({
    apiName
  });
  const isUngatedForInbound25 = useAppSelector(isUngatedForInbound25Selector);
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  if (!isAppReady || !product || !apiName || !skuId) {
    return null;
  }
  const productName = productNameOverride || productLine;
  const Title = /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    children: [/*#__PURE__*/_jsx(ProductCardBadge, {
      apiName: apiName
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: "start",
      gap: "200",
      children: [isUngatedForInbound25 ? null : productName && /*#__PURE__*/_jsx(ProductIcon, {
        iconStyle: {
          responsive: false,
          blockSize: '24px'
        },
        name: productName,
        tier: productLevel
      }), title && /*#__PURE__*/_jsx(SectionTitle, {
        children: title
      })]
    })]
  });
  const Term = shouldShowTermSelector && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [41],
    widths: ['100%'],
    children: /*#__PURE__*/_jsx(DetailedTermSelector, {
      apiName: apiName,
      onChange: setQueryParamTerm,
      activeTerm: queryParamTerm,
      trackingSource: trackingSource
    })
  });
  const CTAs = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [47],
    widths: ['100%'],
    children: /*#__PURE__*/_jsx(ErrorBoundary, {
      fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
        apiName: apiName,
        screen: screen,
        trackingSource: trackingSource
      }),
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        justifyContent: "center",
        gap: "200",
        inlineSize: "100%",
        children: /*#__PURE__*/_jsx(ProductCardCTAs, {
          apiName: apiName,
          screen: screen,
          trackingSource: trackingSource,
          skipInterstitialPage: skipInterstitialPage,
          hasHigherTieredProduct: hasHigherTieredProduct,
          isHubspotFree: isHubspotFree,
          purchaseMotions: purchaseMotions
        })
      })
    })
  });
  const Features = /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
    isLoading: !isAppReady,
    heights: [48, 48],
    widths: ['100%', '100%'],
    children: shouldShowSuiteObjectives ? /*#__PURE__*/_jsx(SuiteFeatures, {
      productLevel: productLevel,
      loading: !isAppReady,
      isCollapsible: isMobile
    }) : /*#__PURE__*/_jsx(ProductTileFeaturesContainer, {
      apiName: apiName,
      isCollapsible: isMobile
    })
  });
  const Footer = skuId && /*#__PURE__*/_jsx(Flex, {
    flexDirection: "column",
    alignItems: "center",
    children: /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
      queryParamTerm: queryParamTerm,
      skuIds: [skuId],
      trackingSource: trackingSource,
      includesOnboardingCost: false
    })
  });
  return /*#__PURE__*/_jsx(MerchCardLayoutUI, {
    "data-selenium-id": `${apiName}-merch-card`,
    "data-test-id": `${apiName}-merch-card`,
    columnStart: columnStart,
    Title: Title,
    Subheading: description || null,
    Pricing: /*#__PURE__*/_jsx(PriceDisplayContainer, {
      skuId: skuId,
      queryParamTerm: queryParamTerm,
      apiName: apiName,
      isHubspotFree: isHubspotFree
    }),
    Seats: /*#__PURE__*/_jsx(IncludedSeatsDisplayContainerV1, {
      skuId: skuId
    }),
    Term: Term,
    CTAs: CTAs,
    Features: Features,
    Footer: Footer,
    isUngatedForInbound25: isUngatedForInbound25
  });
};